import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import AddressBookPicker from '@/components/AddressBookPicker';
import ZipAutofillInput from '@/components/ZipAutofillInput';
import DeclaredValueAndCoverage from '@/components/DeclaredValueAndCoverage';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from 'sonner';
import { 
  Zap, MapPin, Clock, DollarSign, Shield, Package,
  Loader2, Car, Bike, Truck, AlertCircle, CheckCircle,
  FileText, ArrowRight
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// SelleSend markup structure
const MARKUP = {
  base_fee: 2.99,
  percentage: 0.045, // 4.5%
  insurance_rates: {
    basic: { limit: 100, rate: 0.99 },
    standard: { limit: 500, rate: 2.99 },
    premium: { limit: 1000, rate: 4.99 },
    max: { limit: 2500, rate: 9.99 },
  }
};

const VEHICLE_TYPES = [
  { value: 'bike', label: 'Bike/Scooter', icon: Bike, maxWeight: 10, description: 'Small packages up to 10 lbs' },
  { value: 'car', label: 'Car', icon: Car, maxWeight: 30, description: 'Medium packages up to 30 lbs' },
  { value: 'suv', label: 'SUV/Van', icon: Car, maxWeight: 70, description: 'Large packages up to 70 lbs' },
  { value: 'truck', label: 'Cargo Van', icon: Truck, maxWeight: 200, description: 'Bulk items up to 200 lbs' },
];

const SameDayDeliveryPage = () => {
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1);
  const [quote, setQuote] = useState(null);
  const [deliveryId, setDeliveryId] = useState(null);
  const { getAuthHeaders, user, token } = useAuth();

  const [formData, setFormData] = useState({
    // Pickup
    pickup_name: '',
    pickup_phone: '',
    pickup_address: '',
    pickup_apt: '',
    pickup_city: '',
    pickup_state: '',
    pickup_zip: '',
    pickup_instructions: '',
    
    // Dropoff
    dropoff_name: '',
    dropoff_phone: '',
    dropoff_address: '',
    dropoff_apt: '',
    dropoff_city: '',
    dropoff_state: '',
    dropoff_zip: '',
    dropoff_instructions: '',
    
    // Package
    package_description: '',
    weight: '',
    vehicle_type: 'car',
    requires_signature: false,
    contains_alcohol: false,
    is_fragile: false,
    
    // Insurance
    insurance_type: 'basic',
    declared_value: '',
    
    // Terms
    accept_terms: false,
    accept_liability: false,
  });

  const getQuote = async () => {
    if (!formData.pickup_zip || !formData.dropoff_zip) {
      toast.error('Please enter pickup and dropoff ZIP codes');
      return;
    }
    
    setLoading(true);
    
    try {
      const response = await axios.post(`${API}/sameday/quote`, {
        pickup_zip: formData.pickup_zip,
        dropoff_zip: formData.dropoff_zip,
        vehicle_type: formData.vehicle_type,
        weight: parseFloat(formData.weight) || 5,
        insurance_type: formData.insurance_type,
        declared_value: parseFloat(formData.declared_value) || 0,
      }, {
        headers: getAuthHeaders()
      });
      
      setQuote(response.data);
      setStep(2);
      toast.success('Quote generated');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to get quote');
    } finally {
      setLoading(false);
    }
  };

  const confirmDelivery = async () => {
    if (!formData.accept_terms || !formData.accept_liability) {
      toast.error('Please accept the terms and conditions');
      return;
    }
    
    setLoading(true);
    
    try {
      const response = await axios.post(`${API}/sameday/book`, {
        ...formData,
        quote_id: quote.id,
        total_cost: quote.total,
      }, {
        headers: getAuthHeaders()
      });
      
      setDeliveryId(response.data.id);
      setStep(3);
      toast.success('Delivery booked successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to book delivery');
    } finally {
      setLoading(false);
    }
  };

  const renderAddressForm = (prefix, title) => (
    <div className="space-y-4">
      <h3 className="font-medium text-lg">{title}</h3>
      <AddressBookPicker
        token={token}
        type={prefix === 'pickup' ? 'ship_from' : 'ship_to'}
        testid={`sameday-${prefix}`}
        label={`Address Book · ${title}`}
        value={{
          name: formData[`${prefix}_name`],
          city: formData[`${prefix}_city`],
          state: formData[`${prefix}_state`],
          zip: formData[`${prefix}_zip`],
        }}
        onSelect={(a) => setFormData({
          ...formData,
          [`${prefix}_name`]: a.name || formData[`${prefix}_name`],
          [`${prefix}_phone`]: a.phone || formData[`${prefix}_phone`],
          [`${prefix}_address`]: a.street1 || formData[`${prefix}_address`],
          [`${prefix}_apt`]: a.street2 || formData[`${prefix}_apt`] || '',
          [`${prefix}_city`]: a.city || formData[`${prefix}_city`],
          [`${prefix}_state`]: a.state || formData[`${prefix}_state`],
          [`${prefix}_zip`]: (a.zip_code || a.zip || formData[`${prefix}_zip`]),
        })}
      />
      <div className="grid sm:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Contact Name *</Label>
          <Input
            value={formData[`${prefix}_name`]}
            onChange={(e) => setFormData({...formData, [`${prefix}_name`]: e.target.value})}
            placeholder="Full name"
            required
            data-testid={`${prefix}-name`}
          />
        </div>
        <div className="space-y-2">
          <Label>Phone *</Label>
          <Input
            value={formData[`${prefix}_phone`]}
            onChange={(e) => setFormData({...formData, [`${prefix}_phone`]: e.target.value})}
            placeholder="(555) 123-4567"
            required
            data-testid={`${prefix}-phone`}
          />
        </div>
      </div>
      <div className="space-y-2">
        <Label>Street Address *</Label>
        <Input
          value={formData[`${prefix}_address`]}
          onChange={(e) => setFormData({...formData, [`${prefix}_address`]: e.target.value})}
          placeholder="123 Main St"
          required
          data-testid={`${prefix}-address`}
        />
      </div>
      <div className="grid sm:grid-cols-4 gap-4">
        <div className="space-y-2">
          <Label>Apt/Suite</Label>
          <Input
            value={formData[`${prefix}_apt`]}
            onChange={(e) => setFormData({...formData, [`${prefix}_apt`]: e.target.value})}
            placeholder="Apt 4B"
          />
        </div>
        <div className="space-y-2">
          <Label>City *</Label>
          <Input
            value={formData[`${prefix}_city`]}
            onChange={(e) => setFormData({...formData, [`${prefix}_city`]: e.target.value})}
            placeholder="City"
            required
            data-testid={`${prefix}-city`}
          />
        </div>
        <div className="space-y-2">
          <Label>State *</Label>
          <Input
            value={formData[`${prefix}_state`]}
            onChange={(e) => setFormData({...formData, [`${prefix}_state`]: e.target.value})}
            placeholder="CA"
            maxLength={2}
            required
            data-testid={`${prefix}-state`}
          />
        </div>
        <div className="space-y-2">
          <Label>ZIP *</Label>
          <ZipAutofillInput
            value={formData[`${prefix}_zip`]}
            onChange={(v) => setFormData({...formData, [`${prefix}_zip`]: v})}
            onResolve={(g) => setFormData((f) => ({
              ...f,
              [`${prefix}_city`]: f[`${prefix}_city`] || g.city || '',
              [`${prefix}_state`]: f[`${prefix}_state`] || g.state_abbr || '',
            }))}
            placeholder="90001"
            testid={`${prefix}-zip`}
          />
        </div>
      </div>
      <div className="space-y-2">
        <Label>Delivery Instructions</Label>
        <Textarea
          value={formData[`${prefix}_instructions`]}
          onChange={(e) => setFormData({...formData, [`${prefix}_instructions`]: e.target.value})}
          placeholder="Gate code, building entrance, etc."
          rows={2}
        />
      </div>
    </div>
  );

  return (
    <div className="max-w-4xl mx-auto space-y-8 animate-fade-in" data-testid="sameday-delivery-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl md:text-3xl font-semibold tracking-tight flex items-center gap-2">
            <Zap className="w-8 h-8 text-amber-500" />
            Same-Day Delivery
          </h1>
          <p className="text-slate-500 mt-1">On-demand delivery powered by SelleSend</p>
        </div>
        <Badge className="bg-amber-500 text-white">
          <Clock className="w-3 h-3 mr-1" />
          Same Day
        </Badge>
      </div>

      {/* Progress Steps */}
      <div className="flex items-center justify-between max-w-md mx-auto">
        {['Details', 'Review', 'Confirmed'].map((label, index) => (
          <div key={label} className="flex items-center">
            <div className={`step-indicator ${step > index + 1 ? 'completed' : step === index + 1 ? 'active' : 'pending'}`}>
              {step > index + 1 ? <CheckCircle className="w-4 h-4" /> : index + 1}
            </div>
            <span className={`ml-2 text-sm ${step === index + 1 ? 'font-medium' : 'text-slate-500'}`}>
              {label}
            </span>
            {index < 2 && (
              <div className={`w-12 h-0.5 mx-3 ${step > index + 1 ? 'bg-emerald-500' : 'bg-slate-200'}`} />
            )}
          </div>
        ))}
      </div>

      {/* Step 1: Details */}
      {step === 1 && (
        <div className="space-y-6">
          <DeclaredValueAndCoverage
            declaredValue={parseFloat(formData.declared_value) || 0}
            onDeclaredChange={(v) => setFormData({ ...formData, declared_value: String(v) })}
            coverage={formData.coverage_plan || 'A'}
            onCoverageChange={(c) => setFormData({ ...formData, coverage_plan: c })}
          />
          {/* Vehicle Selection */}
          <Card>
            <CardHeader>
              <CardTitle>Select Vehicle Type</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {VEHICLE_TYPES.map((vehicle) => (
                  <div
                    key={vehicle.value}
                    className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${
                      formData.vehicle_type === vehicle.value
                        ? 'border-secondary bg-secondary/5'
                        : 'border-slate-200 dark:border-slate-800 hover:border-slate-300'
                    }`}
                    onClick={() => setFormData({...formData, vehicle_type: vehicle.value})}
                    data-testid={`vehicle-${vehicle.value}`}
                  >
                    <vehicle.icon className="w-8 h-8 mb-2 text-slate-600" />
                    <p className="font-medium">{vehicle.label}</p>
                    <p className="text-xs text-slate-500">{vehicle.description}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Addresses */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MapPin className="w-5 h-5 text-emerald-500" />
                Pickup Location
              </CardTitle>
            </CardHeader>
            <CardContent>
              {renderAddressForm('pickup', 'Pickup Address')}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MapPin className="w-5 h-5 text-blue-500" />
                Dropoff Location
              </CardTitle>
            </CardHeader>
            <CardContent>
              {renderAddressForm('dropoff', 'Dropoff Address')}
            </CardContent>
          </Card>

          {/* Package Details */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Package className="w-5 h-5 text-purple-500" />
                Package Details
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Package Description *</Label>
                  <Input
                    value={formData.package_description}
                    onChange={(e) => setFormData({...formData, package_description: e.target.value})}
                    placeholder="Documents, small box, etc."
                    required
                    data-testid="package-description"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Estimated Weight (lbs)</Label>
                  <Input
                    type="number"
                    value={formData.weight}
                    onChange={(e) => setFormData({...formData, weight: e.target.value})}
                    placeholder="5"
                    data-testid="weight-input"
                  />
                </div>
              </div>

              <div className="flex flex-wrap gap-6">
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="requires_signature"
                    checked={formData.requires_signature}
                    onCheckedChange={(checked) => setFormData({...formData, requires_signature: checked})}
                  />
                  <Label htmlFor="requires_signature" className="cursor-pointer">Signature Required (+$1.99)</Label>
                </div>
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="is_fragile"
                    checked={formData.is_fragile}
                    onCheckedChange={(checked) => setFormData({...formData, is_fragile: checked})}
                  />
                  <Label htmlFor="is_fragile" className="cursor-pointer">Fragile Item (+$2.99)</Label>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Insurance */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Shield className="w-5 h-5 text-emerald-500" />
                Insurance Coverage
              </CardTitle>
              <CardDescription>
                SelleSend insurance protects your package during transit
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
                {Object.entries(MARKUP.insurance_rates).map(([key, value]) => (
                  <div
                    key={key}
                    className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${
                      formData.insurance_type === key
                        ? 'border-emerald-500 bg-emerald-50 dark:bg-emerald-900/20'
                        : 'border-slate-200 dark:border-slate-800'
                    }`}
                    onClick={() => setFormData({...formData, insurance_type: key})}
                    data-testid={`insurance-${key}`}
                  >
                    <p className="font-medium capitalize">{key}</p>
                    <p className="text-lg font-semibold text-emerald-600">${value.rate}</p>
                    <p className="text-xs text-slate-500">Up to ${value.limit}</p>
                  </div>
                ))}
              </div>

              <div className="space-y-2">
                <Label>Declared Value ($)</Label>
                <Input
                  type="number"
                  value={formData.declared_value}
                  onChange={(e) => setFormData({...formData, declared_value: e.target.value})}
                  placeholder="0.00"
                  data-testid="declared-value"
                />
              </div>
            </CardContent>
          </Card>

          <Button onClick={getQuote} className="w-full btn-press" size="lg" disabled={loading} data-testid="get-quote-btn">
            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : (
              <>
                Get Quote
                <ArrowRight className="w-4 h-4 ml-2" />
              </>
            )}
          </Button>
        </div>
      )}

      {/* Step 2: Review & Confirm */}
      {step === 2 && quote && (
        <div className="space-y-6">
          {/* Quote Summary */}
          <Card className="bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 border-amber-200 dark:border-amber-800">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Zap className="w-5 h-5 text-amber-500" />
                Your Quote
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span>Base Delivery (Uber Direct)</span>
                  <span className="tabular-nums">${quote.uber_rate?.toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span>SelleSend Service Fee</span>
                  <span className="tabular-nums">${quote.service_fee?.toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span>Platform Fee (4.5%)</span>
                  <span className="tabular-nums">${quote.platform_fee?.toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span>Insurance ({formData.insurance_type})</span>
                  <span className="tabular-nums">${quote.insurance_cost?.toFixed(2)}</span>
                </div>
                {formData.requires_signature && (
                  <div className="flex justify-between">
                    <span>Signature Required</span>
                    <span className="tabular-nums">$1.99</span>
                  </div>
                )}
                {formData.is_fragile && (
                  <div className="flex justify-between">
                    <span>Fragile Handling</span>
                    <span className="tabular-nums">$2.99</span>
                  </div>
                )}
                <div className="border-t pt-3 flex justify-between text-lg font-semibold">
                  <span>Total</span>
                  <span className="text-amber-600 tabular-nums">${quote.total?.toFixed(2)}</span>
                </div>
              </div>

              <div className="mt-4 p-3 bg-white/50 dark:bg-slate-900/50 rounded-lg">
                <div className="flex items-center gap-2 text-sm">
                  <Clock className="w-4 h-4 text-amber-500" />
                  <span>Estimated delivery: <strong>{quote.estimated_time}</strong></span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Route Summary */}
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="flex-1">
                  <p className="text-sm text-slate-500">From</p>
                  <p className="font-medium">{formData.pickup_address}</p>
                  <p className="text-sm text-slate-500">{formData.pickup_city}, {formData.pickup_state} {formData.pickup_zip}</p>
                </div>
                <ArrowRight className="w-6 h-6 text-slate-400" />
                <div className="flex-1">
                  <p className="text-sm text-slate-500">To</p>
                  <p className="font-medium">{formData.dropoff_address}</p>
                  <p className="text-sm text-slate-500">{formData.dropoff_city}, {formData.dropoff_state} {formData.dropoff_zip}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Terms & Conditions */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5" />
                Terms & Conditions
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="p-4 bg-slate-50 dark:bg-slate-900 rounded-lg text-sm space-y-2 max-h-48 overflow-y-auto">
                <p className="font-medium">SelleSend Same-Day Delivery Terms</p>
                <p>1. Delivery services are provided through Uber Direct integration with SelleSend markup and insurance.</p>
                <p>2. SelleSend charges a service fee of $2.99 plus 4.5% platform fee on all deliveries.</p>
                <p>3. Insurance coverage is provided by SelleSend/SelleSafe/SelleSure as selected.</p>
                <p>4. Never covered: cash and cash equivalents, vehicles, perishables and food, live animals or plants, hazardous materials and weapons. Fragile items, art and antiques need an approval code from the office.</p>
                <p>5. Certain high-risk ZIP codes have lower coverage limits.</p>
                <p>6. Claims must be filed within the reporting window specified in your insurance selection.</p>
                <p>7. Coverage applies while in care, custody, and control of SelleSend and carrier systems.</p>
                <p>8. Resale items may be subject to additional 4.5% transaction fee.</p>
              </div>

              <div className="space-y-3">
                <div className="flex items-start gap-2">
                  <Checkbox
                    id="accept_terms"
                    checked={formData.accept_terms}
                    onCheckedChange={(checked) => setFormData({...formData, accept_terms: checked})}
                  />
                  <Label htmlFor="accept_terms" className="text-sm cursor-pointer">
                    I accept the SelleSend same-day delivery terms and conditions, including the SelleSend, SelleSafe, 
                    SelleSure, Selleze, and SelleCheck coverage terms.
                  </Label>
                </div>

                <div className="flex items-start gap-2">
                  <Checkbox
                    id="accept_liability"
                    checked={formData.accept_liability}
                    onCheckedChange={(checked) => setFormData({...formData, accept_liability: checked})}
                  />
                  <Label htmlFor="accept_liability" className="text-sm cursor-pointer">
                    I acknowledge that I am responsible for accurately describing the package contents and that 
                    false declarations may void insurance coverage.
                  </Label>
                </div>
              </div>
            </CardContent>
          </Card>

          <div className="flex gap-4">
            <Button variant="outline" onClick={() => setStep(1)} className="flex-1">
              Back
            </Button>
            <Button 
              onClick={confirmDelivery} 
              className="flex-1 btn-press bg-amber-500 hover:bg-amber-600"
              disabled={loading || !formData.accept_terms || !formData.accept_liability}
              data-testid="confirm-delivery-btn"
            >
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : (
                <>
                  Confirm Delivery - ${quote.total?.toFixed(2)}
                  <Zap className="w-4 h-4 ml-2" />
                </>
              )}
            </Button>
          </div>
        </div>
      )}

      {/* Step 3: Confirmed */}
      {step === 3 && (
        <Card className="border-emerald-200 bg-emerald-50/50 dark:border-emerald-800 dark:bg-emerald-900/10">
          <CardContent className="pt-6 text-center">
            <div className="w-20 h-20 bg-emerald-100 dark:bg-emerald-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-10 h-10 text-emerald-600" />
            </div>
            <h2 className="text-2xl font-semibold mb-2">Delivery Confirmed!</h2>
            <p className="text-slate-600 dark:text-slate-400 mb-4">
              Your same-day delivery has been scheduled.
            </p>
            <code className="block bg-white dark:bg-slate-900 p-4 rounded-lg font-mono text-sm mb-6">
              Delivery ID: {deliveryId}
            </code>
            
            <div className="p-4 bg-amber-50 dark:bg-amber-900/20 rounded-lg mb-6">
              <div className="flex items-center justify-center gap-2 text-amber-700 dark:text-amber-300">
                <Clock className="w-5 h-5" />
                <span className="font-medium">A driver will be assigned shortly</span>
              </div>
            </div>

            <div className="flex gap-4 justify-center">
              <Button variant="outline" onClick={() => { setStep(1); setQuote(null); setDeliveryId(null); }}>
                New Delivery
              </Button>
              <Button onClick={() => window.location.href = '/dashboard/shipments'}>
                View All Deliveries
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default SameDayDeliveryPage;
